import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { skipToken } from "@reduxjs/toolkit/query";
import axios from "axios";
import moment from "moment";
import qs from "qs";
import { useState, useRef } from "react";
import {
  Tab,
  Tabs,
  Card,
  ListGroup,
  OverlayTrigger,
  Tooltip,
  Button,
  Popover,
} from "react-bootstrap";
import { Link } from "react-router-dom";

import { PERMISSION_AREAS } from "api/common/types/auth.types";
import { useListTdsQuery } from "api/memberPortal/technicalDecisions/technicalDecisions.api";
import {
  useIsErtTeamMemberQuery,
  useIsTrrtTeamMemberQuery,
} from "api/memberPortal/virtualTeam/virtualTeam.api";
import ResponsiveMaterialTable from "components/UI/MaterialTable/ResponsiveMaterialTable";
import EarReviewsTable from "features/memberPortal/EAR/components/EarReviewsTable/EarReviewsTable";
import { useAuth } from "hooks/useAuth";
import { checkPermissions } from "utils/permissions";

import TQTable from "../TRRTTableModule/TQTable";

export default function SMETabs() {
  const [earCount, setEarCount] = useState(0);
  const [trrtCount, setTrrtCount] = useState(0);
  const trrtRef = useRef();
  const { authToken, permissions } = useAuth();

  const canViewTds = checkPermissions({
    permissions,
    area: PERMISSION_AREAS.TECHNICAL_DECISIONS,
  });

  const { data: tds } = useListTdsQuery(canViewTds ? undefined : skipToken);

  const { data: isErtMember } = useIsErtTeamMemberQuery();
  const { data: isTrrtMember } = useIsTrrtTeamMemberQuery();

  const tdColumns = [
    {
      title: "Item No.",
      field: "td_id",
      // @ts-ignore - this will be yeeted for datagrid
      render: (rowData) => (
        <Link
          to={`technical-decisions/details/${rowData.identifier}`}
          className="text-decoration-underline"
        >
          {rowData?.identifier}
        </Link>
      ),
    },
    { title: "Title", field: "title" },
    {
      title: "Protection Profiles",
      field: "protection_profile",
      // @ts-ignore - this will be yeeted for datagrid
      render: (rowData) =>
        rowData?.protection_profile?.length > 0 ? (
          <ListGroup>
            {/* @ts-ignore - this will be yeeted for datagrid */}
            {rowData?.protection_profile?.map((projpp) => (
              <OverlayTrigger
                key={projpp.id}
                placement="top"
                overlay={<Tooltip>{projpp.pp_name}</Tooltip>}
              >
                <Link
                  to={`/community/protectionprofiles/details/${projpp.id}`}
                  className="text-decoration-underline"
                >
                  <ListGroup.Item disabled>
                    {projpp.pp_common_name}
                  </ListGroup.Item>
                </Link>
              </OverlayTrigger>
            ))}
          </ListGroup>
        ) : (
          <>No Protection Profiles</>
        ),
    },
    { title: "Status", field: "status" },
    {
      title: "Last Modified",
      // @ts-ignore - this will be yeeted for datagrid
      render: (rowData) => moment.utc(rowData.modified_on).format("MM/DD/YYYY"),
    },
    {
      filtering: false,
      sorting: false,
      // @ts-ignore - this will be yeeted for datagrid
      render: (rowData) => (
        <OverlayTrigger
          trigger="click"
          rootClose
          placement="bottom"
          key="bottom"
          overlay={
            <Popover>
              <Popover.Body>
                <Link
                  to={`technical-decisions/details/${rowData.identifier}`}
                  relative="route"
                  className="text-decoration-none"
                >
                  <Button variant="link" className="m-1 p-0 btn-link">
                    <VisibilityIcon color="action" /> View
                  </Button>
                  <Button variant="link" className="m-1 p-0 btn-link">
                    <EditIcon color="action" /> Edit
                  </Button>
                  <Button variant="link" className="m-1 p-0 btn-link">
                    <DeleteIcon color="action" /> Delete
                  </Button>
                </Link>
              </Popover.Body>
            </Popover>
          }
        >
          <Button variant="link">
            <MoreVertIcon className="float-end" />
          </Button>
        </OverlayTrigger>
      ),
    },
  ];

  const options = {
    sorting: true,
    columnsButton: true,
    pageSize: 20,
  };

  return (
    <Card>
      {isTrrtMember || isErtMember ? (
        <Card.Body>
          <Tabs
            defaultActiveKey={isErtMember ? "escalated-ears" : "TRRTs"}
            className="mb-3"
          >
            {isErtMember && (
              <Tab
                eventKey="escalated-ears"
                title={
                  <div className="d-flex align-items-center">
                    Escalated Ears
                    <h4 className="text-primary ms-2 my-0 fw-bold">
                      {earCount}
                    </h4>
                  </div>
                }
              >
                {/* @ts-ignore - the underlying table needs to be typed properly */}
                <EarReviewsTable
                  refetch={`${isErtMember || ""} ${isTrrtMember || ""}`}
                  setEarCount={setEarCount}
                />
              </Tab>
            )}
            {permissions?.role_permissions?.find(
              (permission) => permission.area === "Technical Decisions",
            )?.read && (
              <Tab
                eventKey="TDs"
                title={
                  <div className="d-flex align-items-center">
                    {"TD's"}
                    <h4 className="text-primary ms-2 my-0 fw-bold">
                      {tds?.count}
                    </h4>
                  </div>
                }
              >
                <ResponsiveMaterialTable
                  title="Technical Decisions"
                  options={options}
                  // @ts-ignore - this will be yeeted for datagrid
                  data={(query) =>
                    new Promise((resolve, reject) => {
                      // Extract the necessary information from the query object
                      const {
                        page,
                        pageSize,
                        search,
                        filters,
                        orderBy,
                        orderDirection,
                      } = query;

                      // @ts-ignore - this will be yeeted for datagrid
                      let newFilters = filters.map((filter) => {
                        let value = "";
                        if (Array.isArray(filter.value)) {
                          value = filter.value;
                        } else {
                          value = filter.value.replace(/['"]+/g, "");
                        }
                        return `"${filter.column.field.replace(/\./g, "__")}":"${value}"`;
                      });
                      const params = {
                        offset: page * pageSize,
                        limit: pageSize,
                        search: search,
                        filters: newFilters,
                        orderBy: orderBy?.field,
                        orderDirection: orderDirection,
                      };
                      axios
                        .get(
                          `${import.meta.env.VITE_DJANGO_ENDPOINT}technical-decision/?${qs.stringify(params, { arrayFormat: "comma" })}`,
                          {
                            withCredentials: true,
                            headers: {
                              Authorization: `Token ${authToken}`,
                            },
                          },
                        )
                        .then((response) => {
                          resolve({
                            data: response.data.results,
                            page: page,
                            totalCount: response.data.count,
                          });
                        })
                        .catch((error) => {
                          reject(error);
                        });
                    })
                  }
                  columns={tdColumns}
                />
              </Tab>
            )}
            {isTrrtMember && (
              <Tab
                eventKey="TRRTs"
                title={
                  <div className="d-flex align-items-center">
                    TRRTs
                    <h4 className="text-primary ms-2 my-0 fw-bold">
                      {trrtCount}
                    </h4>
                  </div>
                }
              >
                <TQTable tableRef={trrtRef} setTrrtCount={setTrrtCount} />
              </Tab>
            )}
          </Tabs>
        </Card.Body>
      ) : (
        <Card.Body>
          <p>Not a Member of a TRRT or ERT Team</p>
        </Card.Body>
      )}
    </Card>
  );
}
